import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Phone, Send } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL_HREF } from "@/lib/contact";
import { track } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RequestDialog({
  triggerClassName,
  triggerTestId = "open-request-dialog",
  triggerIcon = null,
  triggerLabel = "Richiedi Intervento",
}) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", location: "", vehicle: "", notes: "" });
  // Honeypot field — bots fill ALL fields, humans leave this hidden one empty.
  const [website, setWebsite] = useState("");

  const update = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    // Anti-spam: silently drop submissions that filled the honeypot.
    if (website) {
      setOpen(false);
      return;
    }
    if (!form.name || !form.phone || !form.location) {
      toast.error("Compila nome, telefono e posizione per continuare.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/intervention-requests`, form);
      track.leadSubmit();
      toast.success("Richiesta inviata! Ti richiameremo a brevissimo.", { duration: 5000 });
      setOpen(false);
      setForm({ name: "", phone: "", location: "", vehicle: "", notes: "" });
      setWebsite("");
    } catch (err) {
      console.error(err);
      toast.error("Errore nell'invio. Chiamaci direttamente al " + PHONE_DISPLAY);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button type="button" data-testid={triggerTestId} className={triggerClassName}>
          {triggerIcon}
          {triggerLabel}
        </button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="request-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-navy">Richiedi Intervento</DialogTitle>
          <DialogDescription>
            Compila i dati: ti richiamiamo entro pochi minuti. Per emergenze, chiama subito{" "}
            <a href={PHONE_TEL_HREF} className="font-bold text-brand-orange underline">
              {PHONE_DISPLAY}
            </a>.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="grid gap-4 mt-2">
          {/* Honeypot field — invisible to users, lures bots. */}
          <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: 1, height: 1, overflow: "hidden" }}>
            <label htmlFor="website-honeypot">Sito web</label>
            <input
              id="website-honeypot"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
          </div>
          <Field id="name" label="Nome e cognome *">
            <Input id="name" data-testid="request-name" value={form.name} onChange={update("name")} placeholder="Mario Rossi" required />
          </Field>
          <Field id="phone" label="Telefono *">
            <Input id="phone" data-testid="request-phone" type="tel" value={form.phone} onChange={update("phone")} placeholder="es. 333 1234567" required />
          </Field>
          <Field id="location" label="Posizione attuale *">
            <Input id="location" data-testid="request-location" value={form.location} onChange={update("location")} placeholder="Indirizzo, zona o riferimento" required />
          </Field>
          <Field id="vehicle" label="Veicolo">
            <Input id="vehicle" data-testid="request-vehicle" value={form.vehicle} onChange={update("vehicle")} placeholder="Auto, moto, modello..." />
          </Field>
          <Field id="notes" label="Descrizione del problema">
            <Textarea id="notes" data-testid="request-notes" value={form.notes} onChange={update("notes")} placeholder="Es. Batteria scarica, gomma forata, incidente leggero..." rows={3} />
          </Field>

          <DialogFooter className="flex-col sm:flex-row gap-2 mt-2">
            <a
              href={PHONE_TEL_HREF}
              onClick={() => track.phoneCall("request_dialog_fallback")}
              data-testid="request-call-fallback"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-brand-orange text-white px-5 py-3 font-bold hover:brightness-110 transition"
            >
              <Phone className="h-4 w-4" /> Chiama Ora
            </a>
            <button
              type="submit"
              disabled={submitting}
              data-testid="request-submit-button"
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-navy text-white px-5 py-3 font-bold hover:bg-navy/90 transition disabled:opacity-60"
            >
              <Send className="h-4 w-4" /> {submitting ? "Invio..." : "Invia richiesta"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ id, label, children }) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id} className="text-navy font-semibold text-sm">{label}</Label>
      {children}
    </div>
  );
}
